import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useLanguage, useAuth } from '@/lib/context';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Briefcase, Search, MapPin, Clock, DollarSign, Eye, Users,
  ArrowRight, Filter, Plus, Sparkles, Home, ChevronRight, Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const HankedPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [country, setCountry] = useState(searchParams.get('country') || '');
  const [remoteOnly, setRemoteOnly] = useState(false);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('listing_type', 'procurement');
      if (category) params.append('category', category);
      if (country) params.append('country', country);
      if (remoteOnly) params.append('remote_only', 'true');
      if (search) params.append('skills', search);

      const res = await fetch(`${API_URL}/api/marketplace/listings?${params}`);
      const data = await res.json();
      setListings(data);
    } catch (err) {
      console.error('Failed to fetch hanked:', err);
    } finally {
      setLoading(false);
    }
  }, [category, country, remoteOnly, search]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-blue-600 flex items-center gap-1">
            <Home className="w-4 h-4" /> {t('common.home')}
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <Link to="/marketplace" className="text-gray-500 hover:text-blue-600">
            Marketplace
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className="text-gray-900 font-medium">{t('marketplace.hanked.title')}</span>
        </div>
      </div>

      {/* Hero Header */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Briefcase className="w-10 h-10" />
                <h1 className="text-3xl font-bold">{t('marketplace.hanked.title')}</h1>
              </div>
              <p className="text-orange-100 max-w-xl">{t('marketplace.hanked.subtitle')}</p>
            </div>
            {user && (
              <Button 
                onClick={() => navigate('/marketplace/new?type=procurement')}
                className="bg-white text-orange-600 hover:bg-orange-50"
                data-testid="post-hanked-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('marketplace.hanked.postNew')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('common.search')}
                className="pl-10"
                onKeyDown={(e) => e.key === 'Enter' && fetchListings()}
                data-testid="hanked-search-input"
              />
            </div>
            
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('marketplace.hanked.filters.category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="construction">Construction</SelectItem>
                <SelectItem value="electrical">Electrical</SelectItem>
                <SelectItem value="plumbing">Plumbing</SelectItem>
                <SelectItem value="it">IT & Tech</SelectItem>
                <SelectItem value="logistics">Logistics</SelectItem>
                <SelectItem value="manufacturing">Manufacturing</SelectItem>
              </SelectContent>
            </Select>

            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('marketplace.hanked.filters.location')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="Estonia">Estonia</SelectItem>
                <SelectItem value="Finland">Finland</SelectItem>
                <SelectItem value="Sweden">Sweden</SelectItem>
                <SelectItem value="Norway">Norway</SelectItem>
                <SelectItem value="Germany">Germany</SelectItem>
              </SelectContent>
            </Select>

            <Button 
              variant={remoteOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setRemoteOnly(!remoteOnly)}
            >
              {t('marketplace.hanked.filters.remote')}
            </Button>

            <Button onClick={fetchListings} data-testid="hanked-search-btn">
              <Search className="w-4 h-4 mr-2" />
              {t('common.search')}
            </Button>
          </div>
        </div>
      </div>

      {/* Listings */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-500">{t('common.loading')}</p>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700">{t('common.noResults')}</h3>
            <p className="text-gray-500 mt-2">Try different filters or post a new project</p>
            {user && (
              <Button 
                className="mt-4 bg-orange-500 hover:bg-orange-600"
                onClick={() => navigate('/marketplace/new?type=procurement')}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('marketplace.hanked.postNew')}
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {listings.map((listing) => (
              <ListingCard 
                key={listing.id} 
                listing={listing} 
                onClick={() => navigate(`/marketplace/listing/${listing.id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ListingCard = ({ listing, onClick }) => {
  const { t } = useLanguage();
  
  return (
    <Card 
      className="hover:shadow-lg transition-shadow cursor-pointer"
      onClick={onClick}
      data-testid={`listing-card-${listing.id}`}
    >
      <CardContent className="p-6">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {listing.ai_recommended && (
                <Badge className="bg-purple-100 text-purple-700">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {t('marketplace.listing.aiRecommended')}
                </Badge>
              )}
              {listing.owner_verified && (
                <Badge className="bg-green-100 text-green-700">
                  <Star className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              )}
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{listing.title}</h3>
            <p className="text-gray-600 text-sm line-clamp-2 mb-3">{listing.description}</p>
            
            <div className="flex flex-wrap gap-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {listing.city || listing.country}
                {listing.remote_possible && ' (Remote)'}
              </span>
              {(listing.budget_min || listing.budget_max) && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  {listing.budget_min || 0} - {listing.budget_max || '∞'} {listing.currency}
                </span>
              )}
              {listing.deadline && (
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {t('marketplace.listing.deadline')}: {new Date(listing.deadline).toLocaleDateString()}
                </span>
              )}
            </div>

            {listing.required_skills?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {listing.required_skills.slice(0, 5).map((skill, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {skill}
                  </Badge>
                ))}
                {listing.required_skills.length > 5 && (
                  <Badge variant="outline" className="text-xs">
                    +{listing.required_skills.length - 5}
                  </Badge>
                )}
              </div>
            )}
          </div>

          <div className="text-right shrink-0">
            <p className="text-sm text-gray-500 mb-2">
              {listing.owner_name || 'Company'}
            </p>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {listing.view_count}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {listing.application_count}
              </span>
            </div>
            <Button variant="outline" size="sm" className="mt-3">
              {t('marketplace.listing.viewDetails')}
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default HankedPage;
